import React from "react";
import { GlassRockerDisplaySvg, type GlassRockerDisplaySvgProps } from "./GlasButtonFrontView";
import { MechanicalPushButtonSvg } from "./SimpleButtonFrontView";
import type { C1_ButtonUiTemplate } from "../data/C1_ProductDefinitions";
import type { C1_ButtonColor } from "../types/C1_EloTypes";

export type ButtonFrontViewVariant = "glass" | "simple";

export interface ButtonFrontViewProps extends GlassRockerDisplaySvgProps {
  variant?: ButtonFrontViewVariant;
  uiTemplate?: C1_ButtonUiTemplate;
  className?: string;
  style?: React.CSSProperties;
  frameColor?: string;
  insertColor?: string;
  accentColor?: string;
  color?: C1_ButtonColor;
}

/** Gemeinsamer Einstiegspunkt für Glas- und Standardtaster. */
export function ButtonFrontView({
  variant = "glass",
  uiTemplate,
  frameColor,
  insertColor,
  accentColor,
  color = "white",
  ...props
}: ButtonFrontViewProps) {
  const selectedTemplate = uiTemplate ?? variant;
  if (selectedTemplate === "simple") {
    return (
      <MechanicalPushButtonSvg
        {...props}
        frameColor={frameColor}
        insertColor={insertColor}
        accentColor={accentColor}
        color={color}
      />
    );
  }

  if (selectedTemplate === "touch" || selectedTemplate === "panel" || selectedTemplate === "glass") {
    return <GlassRockerDisplaySvg {...props} color={color} />;
  }

  return null;
}

/** Device orientation: horizontal rows run beside each other, with upright labels. */
export function OrientedButtonFrontView({
  horizontal, width = 92, height = 92, x = 0, y = 0, usePhysicalSize = true,
  className, style, contentRotation = 0, ...props
}: Omit<ButtonFrontViewProps, "orientation"> & { horizontal: boolean }) {
  if (!horizontal) return <ButtonFrontView {...props}
    width={width} height={height} x={x} y={y} usePhysicalSize={usePhysicalSize}
    className={className} style={{ height: usePhysicalSize ? `${height}mm` : height, ...style }}
    contentRotation={contentRotation} orientation="horizontal" />;
  return <svg x={x} y={y}
    width={usePhysicalSize ? `${width}mm` : width}
    height={usePhysicalSize ? `${height}mm` : height}
    viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg"
    className={className} style={style}>
    <g transform={`translate(0 ${height}) rotate(-90)`}>
      <ButtonFrontView {...props} width={height} height={width} usePhysicalSize={false}
        orientation="vertical" contentRotation={contentRotation - 90}
        style={{ maxWidth: "none", height: width }} />
    </g>
  </svg>;
}

/** Alias für die bisher verwendete Schreibweise. */
export const ButtonFontView = ButtonFrontView;

export default ButtonFrontView;
